import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, BarChart3, BookUser, Building2, Check, ChevronsUpDown, Clock, Info, Landmark, LayoutGrid, Lock, Settings, ShieldCheck, ShieldHalf } from 'lucide-react';
import { formatDate } from '@zuri/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { cx } from '../lib/format';
import { AppShell, type NavItem } from './AppShell';
import { CompanyAvatar, CompanyLogo, useBrandTheme } from '../components/Brand';
import { MenuItem, Popover } from '../components/ui/Layout';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/States';
import { Button } from '../components/ui/Button';

/** Identidade da empresa ativa + troca de contexto (validada no servidor). */
function CompanySwitcher({ compact }: { compact?: boolean }) {
  const { me, switchCompany } = useSession();
  const navigate = useNavigate();
  const company = me!.activeCompany!;
  const many = me!.memberships.length > 1 || !!me!.platformRole;
  const identity = (
    <div className="min-w-0 flex-1">
      <div className={cx('flex items-center', compact ? 'h-7' : 'h-9')}>
        <CompanyLogo branding={company.branding} name={company.name} height={compact ? 26 : 32} />
      </div>
      {!compact && <div className="mt-1.5 truncate text-[12px] text-ink-3">{company.branding.logoUrl ? `${company.name} · ` : ''}{company.roleName}</div>}
    </div>
  );
  if (!many) return <div className="flex items-center gap-2 px-2 py-1">{identity}</div>;
  return (
    <Popover
      className="w-[272px]"
      trigger={({ toggle, open }) => (
        <button type="button" onClick={toggle} aria-expanded={open} aria-haspopup="menu" aria-label={`Empresa ativa: ${company.name}. Trocar de empresa`} className="flex w-full items-center gap-2 rounded-[14px] px-2 py-1.5 text-left transition-colors hover:bg-hover">
          {identity}
          <ChevronsUpDown className="size-4 shrink-0 text-ink-3" aria-hidden />
        </button>
      )}
    >
      {(close) => (
        <div>
          <div className="eyebrow px-2.5 pb-1.5 pt-1.5">Suas empresas</div>
          {me!.memberships.map((m) => (
            <MenuItem
              key={m.tenantId}
              active={m.tenantId === company.id}
              icon={m.tenantId === company.id ? <Check /> : <CompanyAvatar name={m.tenantName} src={m.logoUrl} size={22} className="rounded-[6px]" />}
              onClick={async () => {
                close();
                if (m.tenantId !== company.id) {
                  await switchCompany(m.tenantId);
                  navigate('/app');
                }
              }}
            >
              {m.tenantName}
              <span className="block text-[11.5px] text-ink-3">{m.roleName}{m.isDemo ? ' · demonstração' : ''}</span>
            </MenuItem>
          ))}
          {me!.platformRole && (
            <>
              <div className="my-1.5 h-px bg-separator" />
              <MenuItem icon={<ShieldHalf />} onClick={() => { close(); navigate('/admin'); }}>
                Administração da plataforma
              </MenuItem>
            </>
          )}
        </div>
      )}
    </Popover>
  );
}

function AccessBanner() {
  const { me } = useSession();
  const access = me?.access;
  const sub = me?.activeCompany?.subscription;
  if (!access) return null;
  let tone: 'orange' | 'red' | 'blue' | null = null;
  let text = access.message;
  if (access.level === 'read_only') tone = 'red';
  else if (access.code === 'past_due_grace') tone = 'orange';
  else if (access.code === 'trial' && sub?.trialEndsAt) {
    tone = 'blue';
    text = `Período de avaliação até ${formatDate(sub.trialEndsAt)} · plano ${sub.planName}.`;
  }
  if (!tone) return null;
  const cls = { orange: 'bg-warning-soft text-warning', red: 'bg-danger-soft text-danger', blue: 'bg-info-soft text-info' }[tone];
  const Icon = access.level === 'read_only' ? Lock : tone === 'orange' ? Clock : Info;
  return (
    <div className={cx('flex items-center justify-center gap-2 px-4 py-2 text-center text-[12.5px] font-[560] lg:rounded-[14px]', cls)} role="status">
      <Icon className="size-3.5 shrink-0" aria-hidden />
      {text}
    </div>
  );
}

export function CompanyLayout() {
  const { me, can, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const company = me?.activeCompany ?? null;
  // Identidade da empresa aplicada antes da pintura; sem empresa → identidade neutra.
  useBrandTheme(company?.branding ?? null, company?.name);
  const pending = useQuery({
    queryKey: ['approvals-count', company?.id],
    queryFn: () => api<{ total: number }>('/company/approvals?status=pending&pageSize=1'),
    enabled: !!company && can('approvals.view') && me?.access?.level !== 'blocked',
    refetchInterval: 60_000,
  });

  if (!me) return <Navigate to="/entrar" replace />;
  if (!company) {
    if (me.memberships.length > 0) return <Navigate to="/empresas" replace />;
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <div className="app-backdrop" aria-hidden />
        <div className="card w-full max-w-md">
          <EmptyState
            icon={<Building2 />}
            title="Você ainda não participa de nenhuma empresa"
            description="Peça ao administrador da sua empresa para adicioná-lo, ou crie uma nova empresa."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                {me.platformRole && <Button variant="primary" onClick={() => navigate('/admin')}>Administração da plataforma</Button>}
                <Button onClick={() => void logout()}>Sair</Button>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  const items: NavItem[] = [
    { to: '/app', end: true, label: 'Visão geral', short: 'Início', icon: <LayoutGrid />, primary: true },
    ...(can('payables.view') ? [{ to: '/app/pagar', label: 'Contas a pagar', short: 'Pagar', icon: <ArrowUpRight />, primary: true }] : []),
    ...(can('receivables.view') ? [{ to: '/app/receber', label: 'Contas a receber', short: 'Receber', icon: <ArrowDownLeft />, primary: true }] : []),
    ...(can('approvals.view') ? [{ to: '/app/aprovacoes', label: 'Aprovações', icon: <ShieldCheck />, badge: pending.data?.total, primary: true }] : []),
    ...(can('bank.view') ? [{ to: '/app/bancos', label: 'Contas bancárias', short: 'Bancos', icon: <Landmark /> }] : []),
  ];
  const manage: NavItem[] = [
    ...(can('registry.view') ? [{ to: '/app/cadastros', label: 'Cadastros', icon: <BookUser /> }] : []),
    ...(can('reports.view') ? [{ to: '/app/relatorios', label: 'Relatórios', icon: <BarChart3 /> }] : []),
    { to: '/app/configuracoes', label: 'Configurações', icon: <Settings /> },
  ];

  const blocked = me.access?.level === 'blocked';

  return (
    <AppShell
      header={<CompanySwitcher />}
      contextLabel={company.isDemo ? <Badge tone="violet">Demonstração · dados fictícios</Badge> : undefined}
      nav={[{ items }, { title: 'Gestão', items: manage }]}
      banner={<AccessBanner />}
      mobileTitle={
        <div className="flex min-w-0 items-center gap-2">
          <CompanySwitcher compact />
          {company.isDemo && <Badge tone="violet">Demo</Badge>}
        </div>
      }
    >
      {blocked && !location.pathname.startsWith('/app/configuracoes') ? (
        <div className="card mx-auto mt-10 max-w-lg">
          <EmptyState icon={<Lock />} title="Acesso à empresa bloqueado" description={me.access?.message} action={<Button onClick={() => navigate('/app/configuracoes?aba=assinatura')}>Ver assinatura</Button>} />
        </div>
      ) : (
        <Outlet />
      )}
    </AppShell>
  );
}
